import { uniqueId } from 'lodash';

import { CheckStatus, MatchRequirement } from 'dashboard/models/check';
import {
  EcbsvRelationship,
  GovernmentIdPage,
  VerificationAamva,
  VerificationBusinessWebsite,
  VerificationCaptureMethod,
  VerificationDatabase,
  VerificationDatabaseAadhaar,
  VerificationDatabaseBusiness,
  VerificationDatabaseEcbsv,
  VerificationDatabasePhoneCarrier,
  VerificationDatabaseStandard,
  VerificationDatabaseTin,
  VerificationDatabaseVatNumber,
  VerificationDigitalIdUkSharecode,
  VerificationDocument,
  VerificationEmailAddress,
  VerificationGovernmentId,
  VerificationPhoneNumber,
  VerificationSelfie,
} from 'dashboard/models/verification';
import { createPreviewDocumentGeneric } from 'dashboard/modules/CaseModuleBase/previewBuilders/document';
import { ApiModel } from 'lib/interfaces/api';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

export const generateVerificationId = () => uniqueId('ver_');

const createdAt = '2023-01-01T00:00:00.000Z';
const completedAt = '2023-01-01T00:01:00.000Z';

const issuedAt = '2022-01-01T00:00:00.000Z';
const expiresAt = '2028-01-01T00:00:00.000Z';

export function createPreviewVerificationGovernmentId(
  attributes?: Partial<VerificationGovernmentId['attributes']>,
  relationships?: Partial<VerificationGovernmentId['relationships']>,
  id?: string,
): VerificationGovernmentId {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      'entity-confidence-score': 98.10750579833984,
      'entity-confidence-reasons': [],
      'front-photo-url': 'https://i.imgur.com/kRbeYnR.png',
      'back-photo-url': undefined,
      'name-first': 'ALEXANDER J',
      'name-middle': '',
      'name-last': 'SAMPLE',
      birthdate: '1991-11-21',
      'address-street-1': '600 CALIFORNIA STREET',
      'address-street-2': undefined,
      'address-city': 'SAN FRANCISCO',
      'address-subdivision': 'CA',
      'address-postal-code': '06226',
      'issue-date': issuedAt,
      'expiration-date': expiresAt,
      'identification-number': 'I1234562',
      'capture-method': VerificationCaptureMethod.Auto,
      files: [
        {
          page: GovernmentIdPage.Front,
          'normalized-files': [
            {
              url: 'https://i.imgur.com/kRbeYnR.jpg',
              filename: 'Front',
              'file-type': 'image/jpeg',
              'byte-size': 7785,
            },
          ],
          'normalized-url': 'https://i.imgur.com/kRbeYnR.png',
        },
      ],
      checks: [
        {
          name: CheckName.IdInquiryComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdAccountComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdSelfieComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdCompromisedDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdRepeatDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdInconsistentRepeatDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdPoBoxDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdExtractedPropertiesDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdBarcodeDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdDisallowedCountryDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdExpiredDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdUnprocessableSubmissionDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdEntityDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdDisallowedTypeDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdElectronicReplicaDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdAgeComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdAamvaDatabaseLookup,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdRealIdDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdAgeInconsistencyDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdBarcodeInconsistencyDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdExtractionInconsistencyDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdBlurDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdDoubleSideDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdColorInconsistencyDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdValidDatesDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdHandwritingDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdTamperDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdPortraitClarityDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdPortraitDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdPublicFigureDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdGlareDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdMrzDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.IdMrzInconsistencyDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    id: id || generateVerificationId(),
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateGovernmentId,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    type: ApiModel.VerificationGovernmentId,
  };
}

export function createPreviewVerificationSelfie(
  attributes?: Partial<VerificationSelfie['attributes']>,
  relationships?: Partial<VerificationSelfie['relationships']>,
  id?: string,
): VerificationSelfie {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': undefined,
      'entity-confidence-score': 100,
      'entity-confidence-reasons': [],
      'document-similarity-score': 84.85858154296875,
      'selfie-similarity-score-left': 99.72953033447266,
      'selfie-similarity-score-right': 99.78156280517578,
      'capture-method': VerificationCaptureMethod.Auto,
      photos: [
        {
          url: 'https://i.imgur.com/1i5WTBI.png',
          name: 'center_photo',
        },
        {
          url: 'https://i.imgur.com/8Vzk5cB.png',
          name: 'left_photo',
        },
        {
          url: 'https://i.imgur.com/smgdVUx.png',
          name: 'right_photo',
        },
      ],
      checks: [
        {
          name: CheckName.SelfiePoseDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
        },
        {
          name: CheckName.SelfieIdComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieMultipleFacesDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfiePoseRepeatDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieAccountComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieSuspiciousEntityDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieLivenessDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieGlassesDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieGlareDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfiePublicFigureDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.SelfieAgeComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateSelfie,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationSelfie,
  };
}

export function createPreviewVerificationDocument(
  attributes?: Partial<VerificationDocument['attributes']>,
  relationships?: Partial<VerificationDocument['relationships']>,
  id?: string,
): VerificationDocument {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      checks: [
        {
          name: CheckName.DocumentUnprocessableSubmissionDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentExpiredDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentExtractedPropertiesDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentRecencyDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentTypeDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentPortraitDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentFabricationDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentPdfAbnormalFontDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentPdfAnnotationDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentPdfContentTypeDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentPdfEditorDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentPdfInconsistentTimestampDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentJpegOriginalImageDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentImageCopyMoveDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentImageInconsistentTimestampDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentImageSuspiciousMetadataDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DocumentSelfieComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    id: id || generateVerificationId(),
    relationships: {
      document: {
        data: createPreviewDocumentGeneric(),
      },
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDocument,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    type: ApiModel.VerificationDocument,
  };
}

export function createPreviewVerificationDatabase(
  attributes?: Partial<VerificationDatabase['attributes']>,
  relationships?: Partial<VerificationDatabase['relationships']>,
  id?: string,
): VerificationDatabase {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      latitude: 37.7749,
      longitude: -122.4194,
      checks: [
        {
          name: CheckName.DatabaseInquiryComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseAddressDeliverableDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseAddressResidentialDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabasePoBoxDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseDeceasedDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseTwoPlusTwoDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabase,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabase,
  };
}

export function createPreviewVerificationDatabaseAadhaar(
  attributes?: Partial<VerificationDatabaseAadhaar['attributes']>,
  relationships?: Partial<VerificationDatabaseAadhaar['relationships']>,
  id?: string,
): VerificationDatabaseAadhaar {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      'name-first': 'ALEXANDER J',
      'name-last': 'SAMPLE',
      birthdate: '1991-11-21',
      checks: [
        {
          name: CheckName.DatabaseStandardIdentityComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {
            description: 'Testing',
            results: [
              {
                'best-result': 'full',
                status: CheckStatus.Passed,
                properties: ['name_first'],
                'claimed-attributes': [],
                'extracted-attributes': [],
                'normalized-claimed-attributes': ['ALEXANDER J'],
                'normalized-extracted-attributes': ['ALEXANDER J'],
                required: true,
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['name_last'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                'claimed-attributes': [],
                'extracted-attributes': [],
                'normalized-claimed-attributes': ['SAMPLE'],
                'normalized-extracted-attributes': ['SAMPLE'],
                required: true,
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['address_postal_code'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                'claimed-attributes': [
                  {
                    address_postal_code: '06226',
                  },
                ],
                'extracted-attributes': [],
                'normalized-claimed-attributes': ['06226'],
                'normalized-extracted-attributes': ['06226'],
                required: true,
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
            ],
          },
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabaseAadhaar,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabaseAadhaar,
  };
}

export function createPreviewVerificationDatabaseStandard(
  attributes?: Partial<VerificationDatabaseStandard['attributes']>,
  relationships?: Partial<VerificationDatabaseStandard['relationships']>,
  id?: string,
): VerificationDatabaseStandard {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      'name-first': 'ALEXANDER J',
      'name-last': 'SAMPLE',
      birthdate: '1991-11-21',
      'address-postal-code': '06226',
      checks: [
        {
          name: CheckName.DatabaseStandardIdentityComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {
            description: 'Testing',
            results: [
              {
                'best-result': 'full',
                status: CheckStatus.Passed,
                properties: ['name_first'],
                'claimed-attributes': [],
                'extracted-attributes': [],
                'normalized-claimed-attributes': ['ALEXANDER J'],
                'normalized-extracted-attributes': ['ALEXANDER J'],
                required: true,
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['name_last'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                'claimed-attributes': [],
                'extracted-attributes': [],
                'normalized-claimed-attributes': ['SAMPLE'],
                'normalized-extracted-attributes': ['SAMPLE'],
                required: true,
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['address_postal_code'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                required: true,
                'claimed-attributes': [
                  {
                    address_postal_code: '06226',
                  },
                ],
                'extracted-attributes': [],
                'normalized-claimed-attributes': ['06226'],
                'normalized-extracted-attributes': ['06226'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
            ],
          },
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabaseStandard,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabaseStandard,
  };
}

export function createPreviewVerificationDatabaseBusiness(
  attributes?: Partial<VerificationDatabaseBusiness['attributes']>,
  relationships?: Partial<VerificationDatabaseBusiness['relationships']>,
  id?: string,
): VerificationDatabaseBusiness {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'country-code': 'US',
      'created-at': createdAt,
      'completed-at': completedAt,
      'match-group-results': [
        {
          description: 'Business Name, Associated Person, and Full Address',
          status: CheckStatus.Passed,
        },
        {
          description: 'Business Name and Full Address',
          status: CheckStatus.NotApplicable,
        },
      ],
      checks: [
        {
          name: CheckName.DatabaseBusinessIdentityComparison,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {
            description: 'Business Name, Associated Person, and Full Address',
            results: [
              {
                properties: ['business_name'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                required: true,
                'claimed-attributes': [
                  {
                    'business-name': 'ACME CO.',
                  },
                ],
                'extracted-attributes': [
                  {
                    'business-name': 'ACME CO.',
                  },
                ],
                'normalized-claimed-attributes': ['ACME CO.'],
                'normalized-extracted-attributes': ['ACME CO.'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'tokenization',
                    'match-level-minimum': 'partial',
                    'match-level': 'partial',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'doing_business_as',
                    'match-level-minimum': 'partial',
                    'match-level': 'none',
                    status: CheckStatus.Failed,
                  },
                ],
              },
              {
                properties: ['associated_person_name'],
                status: CheckStatus.Passed,
                'best-result': 'partial',
                required: true,
                'claimed-attributes': [
                  {
                    'associated-person-name': 'John Sample',
                  },
                ],
                'extracted-attributes': [
                  {
                    'associated-person-name': 'John Sample',
                  },
                ],
                'normalized-claimed-attributes': ['John Sample'],
                'normalized-extracted-attributes': ['John Sample'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'partial',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'tokenization',
                    'match-level-minimum': 'partial',
                    'match-level': 'none',
                    status: CheckStatus.Failed,
                  },
                ],
              },
              {
                properties: ['address_street_1'],
                status: CheckStatus.Passed,
                'best-result': 'partial',
                required: true,
                'claimed-attributes': [
                  {
                    'address-street-1': '123 Sample Street',
                  },
                ],
                'extracted-attributes': [
                  {
                    'address-street-1': '123 Sample Street',
                  },
                ],
                'normalized-claimed-attributes': ['123 Sample Street'],
                'normalized-extracted-attributes': ['123 Sample Street'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'partial',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'tokenization',
                    'match-level-minimum': 'partial',
                    'match-level': 'partial',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['address_city'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                required: true,
                'claimed-attributes': [
                  {
                    'address-city': 'San Francisco',
                  },
                ],
                'extracted-attributes': [
                  {
                    'address-city': 'SAN FRANCISCO',
                  },
                ],
                'normalized-claimed-attributes': ['San Francisco'],
                'normalized-extracted-attributes': ['San Francisco'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                  {
                    'comparison-type': 'tokenization',
                    'match-level-minimum': 'partial',
                    'match-level': 'partial',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['address_subdivision'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                required: true,
                'claimed-attributes': [
                  {
                    'address-subdivision': 'CA',
                  },
                ],
                'extracted-attributes': [
                  {
                    'address-subdivision': 'CA',
                  },
                ],
                'normalized-claimed-attributes': ['CA'],
                'normalized-extracted-attributes': ['CA'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_similarity',
                    'match-level-minimum': 'full',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
              {
                properties: ['address_postal_code'],
                status: CheckStatus.Passed,
                'best-result': 'full',
                required: true,
                'claimed-attributes': [
                  {
                    'address-postal-code': 'XXXXX',
                  },
                ],
                'extracted-attributes': [
                  {
                    'address-postal-code': 'XXXXX',
                  },
                ],
                'normalized-claimed-attributes': ['XXXXX'],
                'normalized-extracted-attributes': ['XXXXX'],
                'attribute-comparison-results': [
                  {
                    'comparison-type': 'string_difference',
                    'match-level-minimum': 'partial',
                    'match-level': 'full',
                    status: CheckStatus.Passed,
                  },
                ],
              },
            ],
          },
          requirement: MatchRequirement.Required,
        },
        {
          name: CheckName.DatabaseBusinessActiveBusinessDetection,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          requirement: MatchRequirement.Required,
        },
        {
          name: CheckName.DatabaseBusinessAssociatedPersonsComparison,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {
            results: [
              {
                'match-requirement-results': [
                  {
                    properties: ['name_full'],
                    status: CheckStatus.Passed,
                    'best-result': 'partial',
                    required: true,
                    'claimed-attributes': ['John Sample'],
                    'extracted-attributes': ['John Sample'],
                    'normalized-claimed-attributes': ['John Sample'],
                    'normalized-extracted-attributes': ['John Sample'],
                    'attribute-comparison-results': [
                      {
                        'comparison-type': 'string_similarity',
                        'match-level-minimum': 'partial',
                        'match-level': 'partial',
                        status: CheckStatus.Passed,
                      },
                      {
                        'comparison-type': 'tokenization',
                        'match-level-minimum': 'partial',
                        'match-level': 'partial',
                        status: CheckStatus.Passed,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          requirement: MatchRequirement.NotRequired,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabaseBusiness,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabaseBusiness,
  };
}

export function createPreviewVerificationAamva(
  attributes?: Partial<VerificationAamva['attributes']>,
  relationships?: Partial<VerificationAamva['relationships']>,
  id?: string,
): VerificationAamva {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      'name-first': 'ALEXANDER J',
      'name-last': 'SAMPLE',
      birthdate: '1991-11-21',
      'address-postal-code': '06226',
      'issue-date': issuedAt,
      'expiration-date': expiresAt,
      'identification-number': 'I1234562',
      checks: [
        {
          name: CheckName.AamvaIdentificationNumberComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.AamvaNameComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.AamvaBirthdateComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.AamvaAddressComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.AamvaIssueDateComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.AamvaExpirationDateComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateAamva,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationAamva,
  };
}

export function createPreviewVerificationDigitalIdUkSharecode(
  attributes?: Partial<VerificationDigitalIdUkSharecode['attributes']>,
  relationships?: Partial<VerificationDigitalIdUkSharecode['relationships']>,
  id?: string,
): VerificationDigitalIdUkSharecode {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      sharecode: 'ABC-DEF-12G',
      birthdate: '1991-11-21',
      'created-at': createdAt,
      'completed-at': completedAt,
      'name-full': 'ALEXANDER J SAMPLE',
      'reference-number': 'AB-CDE1H23-IJ',
      'right-to-work-details':
        'ALEXANDER J SAMPLE They can work in the UK. Details They can work in any job. There is no limit on how long they can stay in the UK.',
      'center-photo-url': 'https://i.imgur.com/1i5WTBI.png',
      checks: [
        {
          name: CheckName.DigitalIdUkSharecodeServiceAvailableDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DigitalIdUkSharecodeRightToWorkDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DigitalIdUkSharecodeIdentityComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDigitalIdUkSharecode,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDigitalIdUkSharecode,
  };
}

export function createPreviewVerificationDatabaseEcbsv(
  attributes?: Partial<VerificationDatabaseEcbsv['attributes']>,
  relationships?: Partial<VerificationDatabaseEcbsv['relationships']>,
  id?: string,
): VerificationDatabaseEcbsv {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      'name-first': 'ALEXANDER J',
      'name-middle': '',
      'name-last': 'SAMPLE',
      birthdate: '1991-11-21',
      'email-address': 'alex@sample.com',
      relationship: EcbsvRelationship.Parent,
      checks: [
        {
          name: CheckName.DatabaseEcbsvRecordMatch,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseEcbsvDeathRecord,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    id: id || generateVerificationId(),
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabaseEcbsv,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    type: ApiModel.VerificationDatabaseEcbsv,
  };
}

export function createPreviewVerificationDatabasePhoneCarrier(
  attributes?: Partial<VerificationDatabasePhoneCarrier['attributes']>,
  relationships?: Partial<VerificationDatabasePhoneCarrier['relationships']>,
  id?: string,
): VerificationDatabasePhoneCarrier {
  return {
    attributes: {
      status: VerificationStatus.Initiated,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      'name-first': 'ALEXANDER J',
      'name-last': 'SAMPLE',
      birthdate: '1991-11-21',
      'address-street-1': '600 CALIFORNIA STREET',
      'address-street-2': undefined,
      'address-city': 'SAN FRANCISCO',
      'address-subdivision': 'CA',
      'address-postal-code': '06226',
      'phone-number': '12345678910',
      checks: [
        {
          name: CheckName.DatabasePhoneCarrierNameFirstComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabasePhoneCarrierNameLastComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabasePhoneCarrierBirthdateComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabasePhoneCarrierAddressComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabasePhoneCarrier,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabasePhoneCarrier,
  };
}

export function createPreviewVerificationDatabaseTin(
  attributes?: Partial<VerificationDatabaseTin['attributes']>,
  relationships?: Partial<VerificationDatabaseTin['relationships']>,
  id?: string,
): VerificationDatabaseTin {
  return {
    attributes: {
      status: VerificationStatus.Initiated,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'US',
      tin: '987654321',
      'name-first': 'ALEXANDER J',
      'name-last': 'SAMPLE',
      checks: [
        {
          name: CheckName.DatabaseTinDeceasedDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseTinDisallowedTypeDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseTinInvalidFormatDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseTinServiceAvailableDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseTinNameComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabaseTin,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabaseTin,
  };
}

export function createPreviewVerificationDatabaseVatNumber(
  attributes?: Partial<VerificationDatabaseVatNumber['attributes']>,
  relationships?: Partial<VerificationDatabaseVatNumber['relationships']>,
  id?: string,
): VerificationDatabaseVatNumber {
  return {
    attributes: {
      status: VerificationStatus.Initiated,
      'created-at': createdAt,
      'completed-at': completedAt,
      'country-code': 'LU',
      'vat-number': '987654321',
      checks: [
        {
          name: CheckName.DatabaseVatNumberValidDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
        {
          name: CheckName.DatabaseVatNumberServiceAvailableDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateDatabaseVatNumber,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationDatabaseVatNumber,
  };
}

export function createPreviewVerificationEmailAddress(
  attributes?: Partial<VerificationEmailAddress['attributes']>,
  relationships?: Partial<VerificationEmailAddress['relationships']>,
  id?: string,
): VerificationEmailAddress {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'email-address': 'sample@gmail.com',
      checks: [
        {
          name: CheckName.EmailAddressConfirmationCodeComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateEmailAddress,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationEmailAddress,
  };
}

export function createPreviewVerificationPhoneNumber(
  attributes?: Partial<VerificationPhoneNumber['attributes']>,
  relationships?: Partial<VerificationPhoneNumber['relationships']>,
  id?: string,
): VerificationPhoneNumber {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'phone-number': '+1235551234',
      'num-confirmation-code-attempts': 1,
      'name-first': 'ALEXANDER',
      'name-last': 'SAMPLE',
      checks: [
        {
          name: CheckName.PhoneNumberConfirmationCodeComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {},
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplatePhoneNumber,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationPhoneNumber,
  };
}

export function createPreviewVerificationBusinessWebsite(
  attributes?: Partial<VerificationBusinessWebsite['attributes']>,
  relationships?: Partial<VerificationBusinessWebsite['relationships']>,
  id?: string,
): VerificationBusinessWebsite {
  return {
    attributes: {
      status: VerificationStatus.Passed,
      'created-at': createdAt,
      'completed-at': completedAt,
      'business-name': 'ACME CO.',
      'website-url': 'www.acme.com',
      checks: [
        {
          name: CheckName.BusinessWebsiteDomainResolvableDetection,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {
            'ip-addresses': ['100.100.100.100'],
          },
          'redacted-at': undefined,
        },
        {
          name: CheckName.BusinessWebsiteWebsiteComparison,
          requirement: MatchRequirement.Required,
          status: CheckStatus.Passed,
          reasons: [],
          metadata: {
            'browser-resolved-url': 'https://www.acme.com',
            'extracted-business-name-results': [
              {
                'source-url-page-type': 'landing_page',
                'source-url': 'https://www.acme.com',
                'extracted-name': 'ACME CO.',
                'extracted-analysis':
                  'The name "ACME CO." appears multiple times throughout the text',
                'match-level': 'full',
                'match-analysis':
                  'The two business names "ACME CO." and "ACME CO." match exactly. ',
              },
            ],
            'identity-record-results': {
              'business-names': [
                {
                  'source-type': 'landing_page',
                  source: 'https://www.acme.com',
                  'extracted-value': 'ACME CO.',
                  'extracted-analysis':
                    'The name "ACME CO." appears multiple times throughout the text',
                  'match-level': 'full',
                  'match-analysis':
                    'The two business names "ACME CO." and "ACME CO." match exactly. ',
                },
              ],
            },
          },
          'redacted-at': undefined,
        },
      ],
      ...attributes,
    },
    relationships: {
      'list-matches': {
        data: [],
      },
      'verification-template': {
        data: {
          type: ApiModel.VerificationTemplateBusinessWebsite,
          id: 'vtmpl_1234567890',
        },
      },
      ...relationships,
    },
    id: id || generateVerificationId(),
    type: ApiModel.VerificationBusinessWebsite,
  };
}
